import type { BlockNode, Theme } from '@personalizehq/schemas/ir';
import { newId } from './doc.js';
import { isContainer } from './tree.js';

// What the palette inserts. Every default here is a PUBLISHABLE node — the strict schema is the
// law (SPEC §9), and a palette that inserts a block the author must then repair before publish is
// a palette that lies. So `custom`/`progress` get their required explicit extents, media gets its
// `aspect_ratio` and a non-`hug` width, and tokens are picked out of the doc's OWN theme rather
// than hard-coded (token names are the customer's, not ours — fixtures use `font.body`, a
// customer may use `heading-1`).

export const BLOCK_KINDS = [
  'stack',
  'box',
  'scroll',
  'grid',
  'text',
  'image',
  'video',
  'button',
  'spacer',
  'custom',
  'progress',
  'repeat',
] as const;

export type BlockKind = (typeof BLOCK_KINDS)[number];

/** A visible grey placeholder, inline. A remote URL would make the palette depend on the network,
 *  and an empty `src` renders as a broken-image glyph the author reads as a bug. */
export const PLACEHOLDER_IMAGE =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 3 2"><rect width="3" height="2" fill="%23d4d4d8"/></svg>';

function firstToken(record: Record<string, unknown>): string {
  // A theme with no colours cannot produce a publishable text block either way; the empty string
  // fails the token law loudly at the inspector instead of silently rendering black.
  return Object.keys(record)[0] ?? '';
}

export function newBlock(kind: BlockKind, theme: Theme): BlockNode {
  const id = newId();
  const font = firstToken(theme.fonts);
  const color = firstToken(theme.colors);
  const colors = Object.keys(theme.colors);
  const variant = firstToken(theme.buttons);

  switch (kind) {
    case 'stack':
      return { block: 'stack', id, direction: 'column', gap: 8, align: 'stretch', justify: 'start', children: [] };
    case 'box':
      return { block: 'box', id, align_x: 'center', align_y: 'center', children: [] };
    case 'scroll':
      return { block: 'scroll', id, axis: 'vertical', children: [] };
    case 'grid':
      return { block: 'grid', id, cols: 2, h_spacing: 8, v_spacing: 8, size: { width: 'fill' }, children: [] };
    case 'text':
      return { block: 'text', id, spans: [{ kind: 'lit', value: 'Text' }], font, color };
    case 'image':
      return {
        block: 'image',
        id,
        source: { kind: 'url', url: PLACEHOLDER_IMAGE },
        aspect_ratio: 1.5,
        fit: 'cover',
        size: { width: 'fill' },
      };
    case 'video':
      return {
        block: 'video',
        id,
        source: { kind: 'url', url: '' },
        aspect_ratio: 16 / 9,
        muted: true,
        size: { width: 'fill' },
      };
    case 'button':
      return {
        block: 'button',
        id,
        label: [{ kind: 'lit', value: 'Continue' }],
        variant,
        font,
        actions: [{ type: 'dismiss' }],
      };
    case 'spacer':
      return { block: 'spacer', id, size: { width: 'fill', height: 16 } };
    case 'custom':
      return { block: 'custom', id, identifier: 'my_view', size: { width: 'fill', height: 120 } };
    case 'progress':
      return {
        block: 'progress',
        id,
        segments: 3,
        value: 1,
        gap: 4,
        fill_color: color,
        track_color: colors[1] ?? color,
        size: { width: 'fill', height: 3 },
      };
    case 'repeat':
      return {
        block: 'repeat',
        id,
        source: { kind: 'campaigns', collection: 'campaigns' },
        template: { block: 'text', id: newId(), spans: [{ kind: 'lit', value: 'Item' }], font, color },
        max: 3,
      };
  }
}

/** The bounds meter (p3-authoring-dashboard.md §4). Counts the whole authored tree — `fallback`
 *  subtrees and `repeat` templates included, because they are real nodes at publish and the
 *  gatekeeper counts them. */
export function docStats(root: BlockNode): { nodes: number; depth: number } {
  let nodes = 0;
  let depth = 0;

  const walk = (node: BlockNode, level: number): void => {
    nodes++;
    if (level > depth) depth = level;
    if (isContainer(node)) for (const child of node.children) walk(child, level + 1);
    if (node.block === 'repeat') walk(node.template, level + 1);
    if (node.fallback) walk(node.fallback, level + 1);
  };

  walk(root, 1);
  return { nodes, depth };
}
